<script>
  // One Card Change line within a Binder Update — the feed's row primitive
  // (distinct from the Discord Excerpt Row). Renders quantity, name, set, foil
  // indicator, the typed delta (+N / −N / old→new), and a price for additions.
  import { formatDelta, isFoilFinish, changePrice } from '../../lib/logic/updates-logic.js';

  let { change } = $props();

  const delta = $derived(formatDelta(change));
  const foil = $derived(isFoilFinish(change.finish));
  const price = $derived(changePrice(change));
  const showPrice = $derived(change.change_type === 'added' && price > 0);
</script>

<!--
  Layout: [type-dot] [card-name (flex)] [set-tag] [price]
  The delta (+N / 1→3 / −N) moves into a small badge beside the name
  so the card name is always the focal point.
-->
<div class="change-row change-{change.change_type}">
  <!-- Type indicator: small colored bar on the left -->
  <span class="type-bar" aria-hidden="true"></span>

  <!-- Card name — always first and dominant -->
  <span class="change-name" title={change.card_name || '???'}>
    {change.card_name || '???'}
    {#if foil}<span class="foil-tag" title="Foil">&#9733;</span>{/if}
  </span>

  <!-- Set code: small muted label -->
  {#if change.set_code}
    <span class="change-set" aria-label="Set: {change.set_code.toUpperCase()}">
      {change.set_code.toUpperCase()}
    </span>
  {/if}

  <!-- Delta badge: right-aligned, styled by type -->
  <span class="change-delta" title={change.change_type}>{delta}</span>

  <!-- Price: only for added cards, styled as value -->
  {#if showPrice}
    <span class="change-price">${price.toFixed(2)}</span>
  {/if}
</div>

<style>
  /* ── Row container ── */
  .change-row {
    display: flex;
    align-items: center;
    gap: var(--space-xs, 4px);
    padding: 5px 0;
    border-bottom: 1px solid var(--color-border-subtle, #e0dbd0);
    font-size: 0.875rem;
    min-width: 0;
  }

  .change-row:last-child {
    border-bottom: none;
  }

  /* ── Type bar: 3-px vertical accent on the left ── */
  .type-bar {
    flex-shrink: 0;
    width: 3px;
    height: 14px;
    border-radius: 2px;
    background: currentColor;
    opacity: 0.35;
    margin-right: 2px;
  }

  .change-added .type-bar {
    color: #22c55e;
    opacity: 1;
    background: #22c55e;
  }
  .change-removed .type-bar {
    color: #ef4444;
    opacity: 1;
    background: #ef4444;
  }
  .change-quantity .type-bar {
    color: #f59e0b;
    opacity: 1;
    background: #f59e0b;
  }

  /* ── Card name: dominant ── */
  .change-name {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--color-text, #2c2419);
    font-weight: 500;
    min-width: 0;
  }

  /* ── Foil star ── */
  .foil-tag {
    display: inline;
    font-size: 0.65em;
    color: #f59e0b;
    margin-left: 3px;
    vertical-align: middle;
  }

  /* ── Set code ── */
  .change-set {
    flex-shrink: 0;
    color: var(--color-text-muted, #b5aa96);
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    background: var(--color-bg-tray, #f3efe7);
    border: 1px solid var(--color-border, #e6e0d5);
    border-radius: 4px;
    padding: 1px 5px;
    white-space: nowrap;
  }

  /* ── Delta badge: human-readable, right-aligned ── */
  .change-delta {
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
    font-size: 0.78rem;
    font-weight: 700;
    min-width: 2.2rem;
    text-align: right;
    white-space: nowrap;
  }

  .change-added .change-delta {
    color: #166534;
  }
  .change-removed .change-delta {
    color: #991b1b;
  }
  .change-quantity .change-delta {
    color: #92400e;
  }

  /* ── Price: value-green styling ── */
  .change-price {
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--color-rarity-rare, #a68c5b);
    white-space: nowrap;
  }

  /* ── Mobile: slightly more compact ── */
  @media (max-width: 768px) {
    .change-row {
      font-size: 0.825rem;
      padding: 4px 0;
    }
  }
</style>
